// Public VAPID key is fetched from the server at runtime so the browser
// ALWAYS subscribes with the same key the server signs with. A mismatch
// silently breaks every push (FCM 403 VapidPkHashMismatch).
import { getVapidPublicKey } from "./push.functions";

let _cachedKey: string | null = null;
async function resolveVapidKey(): Promise<string> {
  if (_cachedKey) return _cachedKey;
  const r = await getVapidPublicKey();
  if (!r.key) throw new Error("Server VAPID key missing — admin ko batayein.");
  _cachedKey = r.key;
  return r.key;
}
// Legacy export kept for compatibility (empty string means "fetch dynamically").
export const VAPID_PUBLIC_KEY = "";

function urlB64ToUint8Array(base64String: string) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr;
}

export type SubscribeErrorCode =
  | "no-window" | "no-notification" | "no-sw" | "no-push"
  | "insecure" | "ios-safari" | "in-app-browser"
  | "permission-denied" | "permission-dismissed"
  | "sw-register-failed" | "subscribe-failed";

export type SubscribeResult =
  | { ok: true; sub: PushSubscription }
  | { ok: false; reason: string; code: SubscribeErrorCode; endpoint?: string };

async function findPushRegistration(): Promise<ServiceWorkerRegistration | undefined> {
  const byScope = await navigator.serviceWorker.getRegistration("/");
  if (byScope) return byScope;
  const byScript = await navigator.serviceWorker.getRegistration("/sw-push.js");
  if (byScript) return byScript;
  const all = await navigator.serviceWorker.getRegistrations();
  return all.find((reg) => {
    const worker = reg.active ?? reg.waiting ?? reg.installing;
    return worker?.scriptURL.endsWith("/sw-push.js");
  });
}

function detectBlockingEnv(): { code: SubscribeErrorCode; reason: string } | null {
  if (typeof window === "undefined") return { code: "no-window", reason: "Not running in a browser." };
  if (!window.isSecureContext) return { code: "insecure", reason: "Push notifications require a secure (https) connection." };
  const ua = navigator.userAgent || "";
  const isIOS = /iPad|iPhone|iPod/.test(ua) && !("MSStream" in window);
  // iOS pe SAB browsers (Chrome/Firefox/Edge/Brave) andar se WebKit hain
  // aur Notification/Push API sirf Safari-PWA (Add to Home Screen) me chalta hai.
  // Isliye iOS check ko Notification/SW/Push detection se PEHLE rakhna zaroori hai —
  // warna iOS Chrome pe generic "browser does not support" error dikh jata hai.
  if (isIOS) {
    const isStandalone =
      (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) ||
      (navigator as unknown as { standalone?: boolean }).standalone === true;
    const isSafari = /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS|OPiOS|GSA|DuckDuckGo/.test(ua);
    if (!isSafari) {
      return {
        code: "in-app-browser",
        reason: "iPhone/iPad pe Chrome, Firefox ya Edge me web push kaam nahi karta (Apple ki paabandi hai). Neeche wale share button se link copy karke Safari me kholiye, phir 'Add to Home Screen' karke home screen icon se Subscribe karein.",
      };
    }
    if (!isStandalone) {
      return {
        code: "ios-safari",
        reason: "iPhone/iPad Safari: pehle share button se 'Add to Home Screen' karein, phir home screen icon se site kholkar Subscribe karein. iOS sirf installed (PWA) sites ko push allow karta hai.",
      };
    }
  }
  if (!("Notification" in window)) return { code: "no-notification", reason: "Is browser me notifications support nahi hain. Desktop pe Chrome/Edge/Firefox, Android pe Chrome try karein." };
  if (!("serviceWorker" in navigator)) return { code: "no-sw", reason: "Is browser me service workers support nahi hain." };
  if (!("PushManager" in window)) return { code: "no-push", reason: "Is browser me Web Push support nahi hai." };
  // Facebook / Instagram / TikTok in-app browsers usually block push
  if (/FBAN|FBAV|Instagram|Line|Twitter|TikTok/i.test(ua)) {
    return { code: "in-app-browser", reason: "App ke andar wale browser (Facebook/Instagram) me push kaam nahi karta. Upar right menu se 'Open in Chrome/Safari' choose karein." };
  }
  return null;
}

export async function ensureServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (typeof window === "undefined" || !("serviceWorker" in navigator)) return null;
  try {
    const existing = await findPushRegistration();
    if (existing) return existing;
    return await navigator.serviceWorker.register("/sw-push.js");
  } catch {
    return null;
  }
}

export async function subscribeToPushDetailed(): Promise<SubscribeResult> {
  const blocker = detectBlockingEnv();
  if (blocker) return { ok: false, code: blocker.code, reason: blocker.reason };
  const currentPerm = Notification.permission;
  if (currentPerm === "denied") {
    const staleEndpoint = await clearBrowserPushSubscription();
    return {
      ok: false,
      code: "permission-denied",
      endpoint: staleEndpoint ?? undefined,
      reason: "Chrome ne is site ki notifications Block state me lock kar di hain. Website dobara Allow/Deny prompt force nahi kar sakti. Address bar ke lock icon > Site settings > Notifications ko Allow/Ask karein, page reload karein, phir Subscribe dabayein.",
    };
  }
  const perm = currentPerm === "granted" ? "granted" : await Notification.requestPermission();
  if (perm === "denied") return { ok: false, code: "permission-denied", reason: "Aapne Notifications ko Block kar diya. Address bar ke lock icon > Site settings > Notifications > Allow." };
  if (perm !== "granted") return { ok: false, code: "permission-dismissed", reason: "Permission dialog dismiss ho gayi. Bell dobara press karke 'Allow' choose karein." };
  let reg: ServiceWorkerRegistration | null = null;
  try { reg = await ensureServiceWorker(); } catch { reg = null; }
  if (!reg) return { ok: false, code: "sw-register-failed", reason: "Service worker register nahi ho paya. Page reload karke dobara try karein." };
  try {
    const existing = await reg.pushManager.getSubscription();
    const serverKey = await resolveVapidKey();
    if (existing) {
      // If the existing subscription was made with a DIFFERENT VAPID key,
      // it's dead on the server side — unsubscribe and re-create.
      const existingKey = existing.options?.applicationServerKey;
      const serverKeyBytes = urlB64ToUint8Array(serverKey);
      let matches = false;
      if (existingKey) {
        const a = new Uint8Array(existingKey as ArrayBuffer);
        matches = a.length === serverKeyBytes.length && a.every((v, i) => v === serverKeyBytes[i]);
      }
      if (matches) return { ok: true, sub: existing };
      try { await existing.unsubscribe(); } catch { /* ignore */ }
    }
    const sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlB64ToUint8Array(serverKey),
    });
    return { ok: true, sub };
  } catch (e) {
    return { ok: false, code: "subscribe-failed", reason: `Subscribe fail: ${(e as Error).message}` };
  }
}

// Back-compat wrapper (still used elsewhere)
export async function subscribeToPush(): Promise<PushSubscription | null> {
  const r = await subscribeToPushDetailed();
  return r.ok ? r.sub : null;
}

export async function getExistingSubscription(): Promise<PushSubscription | null> {
  if (typeof window === "undefined" || !("serviceWorker" in navigator)) return null;
  if (("Notification" in window) && Notification.permission !== "granted") return null;
  try {
    const reg = await findPushRegistration();
    if (!reg) return null;
    return await reg.pushManager.getSubscription();
  } catch { return null; }
}

export async function clearBrowserPushSubscription(): Promise<string | null> {
  if (typeof window === "undefined" || !("serviceWorker" in navigator)) return null;
  try {
    const reg = await findPushRegistration();
    const sub = await reg?.pushManager.getSubscription();
    if (!sub) return null;
    const endpoint = sub.endpoint;
    await sub.unsubscribe();
    return endpoint;
  } catch { return null; }
}

export async function unsubscribeFromPush(): Promise<string | null> {
  return clearBrowserPushSubscription();
}

export function serializeSubscription(sub: PushSubscription) {
  const json = sub.toJSON();
  return {
    endpoint: sub.endpoint,
    p256dh: (json.keys && json.keys.p256dh) || "",
    auth: (json.keys && json.keys.auth) || "",
  };
}